import { useState, useEffect } from "react";
import { TextInput, NewNativeSelectInput } from "../ui/Input";
import { fetchAllUsers } from "../../services/apiService/userApi";
import debounce from "lodash.debounce";
import { useSelector } from "react-redux";
import useToaster from "../../hooks/useToaster";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";

const permissionOptions = ["View", "Edit", "None"];

/**
 * Form component for editing user details with validation
 *
 * @param {Object} props - Component props
 * @param {Object} props.userdata - Current user data
 * @param {Function} props.onchange - Input change handler
 * @param {Function} props.onSave - Save handler (called after validation)
 * @param {string} props.active1 - Active parent tab name
 * @param {string} props.active2 - Active child tab name
 * @param {Function} props.oncancel - Cancel handler
 * @param {Array<string>} props.teams - Available team options
 */
const EditUserForm = ({
  userdata,
  onchange,
  onSave,
  active1,
  active2,
  oncancel,
  teams,
}) => {
  // Tracks email validation error state
  const [emailError, setEmailError] = useState("");
  const [initialData, setInitialData] = useState(userdata || {});
  let [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    team: "",
    articleTaskManagement: "",
    issueTaskManagement: "",
    issueOverview: "",
    articleOverview: "",
    settings: "",
  });

  const isDataChanged = () => {
    const keysToCompare = [
      "firstName",
      "lastName",
      "email",
      "team",
      "articleTaskManagement",
      "issueTaskManagement",
      "issueOverview",
      "articleOverview",
      "settings",
    ];

    return keysToCompare.some(
      (key) =>
        JSON.stringify(initialData[key]) !== JSON.stringify(userdata[key])
    );
  };

  // Indicates if current user has admin privileges
  const [isAdmin, setIsAdmin] = useState(false);

  const showToaster = useToaster();
  const { userData } = useSelector((state) => state.user);
  const [isSubmitting, setIsSubmitting] = useState(false);
  /**
   * Determines admin status based on user permissions
   * Sets isAdmin state if user has edit permissions for all key sections
   *
   * @listens userData - Redux user state changes
   * @effect
   */
  useEffect(() => {
    if (
      userData?.articleOverview === "Edit" &&
      userData?.issueOverview === "Edit" &&
      userData?.articleTaskManagement === "Edit" &&
      userData?.issueTaskManagement === "Edit"
    ) {
      setIsAdmin(true);
    }
  }, [userData]);

  /**
   * Validates email uniqueness with debounced API call
   * - Skips validation for empty emails
   * - Excludes current user from duplicate check
   * - Debounces to prevent excessive API calls
   *
   * @listens userdata.email, userdata.id
   * @effect
   */
  useEffect(() => {
    const checkEmail = debounce(async (email) => {
      if (!email) return;

      try {
        const response = await fetchAllUsers(userData?.id);
        const users = response?.data;

        if (!Array.isArray(users)) {
          console.error("Users data is not an array:", users);
          return;
        }

        const emailExists = users.some(
          (user) => user.email === email && user.id !== userdata.id
        );
        setEmailError(emailExists ? "Email already exists." : "");
      } catch (error) {
        console.error("Error checking email:", error);
        setEmailError("An error occurred while checking the email.");
      }
    }, 500);

    checkEmail(userdata.email);
    return () => checkEmail.cancel();
  }, [userdata.email, userdata.id]);

  /**
   * Validates and submits user form data
   * - Checks required fields aren't empty
   * - Verifies email is valid/unique
   * - Calls onSave if validation passes
   * - Shows error toasters for validation failures
   *
   * @param {React.FormEvent<HTMLFormElement>} e - Form submission event
   * @throws {Error} Shows toaster message if:
   *   - Required fields are empty
   *   - Email validation fails
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!navigator.onLine) {
      showToaster("You are currently offline. Please check your internet connection!");
      return;
    }

    setIsSubmitting(true);
    try {
      const requiredFields = [
        { key: "firstName", label: "First Name" },
        { key: "lastName", label: "Last Name" },
        { key: "email", label: "Email Address" },
      ];

      const invalidFields = requiredFields.filter(({ key }) => {
        const value = userdata[key];
        return !value || (typeof value === "string" && value.trim() === "");
      });

      if (invalidFields.length > 0) {
        const invalidFieldNames = invalidFields
          .map(({ label }) => label)
          .join(", ");
        showToaster(
          `The following fields are required and cannot be empty: ${invalidFieldNames}`
        );
        return;
      }

      if (emailError) {
        showToaster(
          "Email Id Already Taken. Please Give An New Email Before Submitting."
        );
        return;
      }

      await onSave(e);
    } catch (error) {
      console.error("Error saving journal:", error);
      showToaster(
        "An error occurred while saving the journal. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateField = (name, value) => {
    let error = "";
    if (!value || (typeof value === "string" && value.trim() === "")) {
      error = "This field is required";
    } else if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        error = "Please enter a valid email address";
      }
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
    return error;
  };
  // Add onChange handler wrapper
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onchange(e);
    validateField(name, value);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-80px)] bg-white overflow-hidden">
      <div className="flex flex-row items-center px-3">
        <button onClick={oncancel}>
          <span className="text-[#868E94] hover:underline text-[13px] font-medium px-1 h-[16px]">
            {active1}
          </span>
        </button>
        <MdOutlineKeyboardArrowRight className="text-[#5D6871] text-[16px]" />
        <button onClick={oncancel}>
          <span className="text-[#868E94] hover:underline text-[13px] font-medium px-1 h-[16px]">
            {active2}
          </span>
        </button>
        <MdOutlineKeyboardArrowRight className="text-[#5D6871] text-[16px]" />
        <span className="text-[#868E94] text-[13px] font-medium px-1 h-[16px]">
          Details
        </span>
      </div>
      <div className="p-5 sticky top-0 h-[60px] bg-white z-10">
        {/*Header Section */}
        <div className="flex items-center gap-2 h-[32px] text-[23px] text-[#20282E]">
          <button
            onClick={oncancel}
            className="flex items-center text-[#20282E] hover:text-gray-900 transition"
          >
            <i className="fas fa-arrow-left text-lg mr-2"></i>
            <span className="text-[23px] text-[#20282E] font-semibold">
              User Details
            </span>
          </button>
        </div>
      </div>

      {/* user information section */}
      <div className={`flex-1 p-5 overflow-y-auto`}>
        <form onSubmit={handleSubmit} className="flex flex-col">
          <p className="text-[18px] h-[24px] font-semibold text-[#5D6871] mb-4">
            User Information
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <TextInput
              label="First name"
              name="firstName"
              value={userdata.firstName}
              onChange={handleInputChange}
              required
              maxLength={100}
              error={errors.firstName}
            />
            <TextInput
              label="Last name"
              name="lastName"
              value={userdata.lastName}
              onChange={handleInputChange}
              required
              error={errors.lastName}
              maxLength={100}
            />
            <TextInput
              label="Email address"
              name="email"
              value={userdata.email}
              onChange={handleInputChange}
              required
              // error={emailError}
              error={emailError || errors.email || ""}
              maxLength={200}
            />
            <NewNativeSelectInput
              label="Team"
              name="team"
              value={userdata.team}
              onChange={handleInputChange}
              options={teams}
              required
              error={errors.team}
            />
          </div>

          {/* user permission section */}
          {isAdmin && (
            <>
              <p className="font-semibold text-[18px] h-[24px] text-[#5D6871] mb-4">
                User Permissions
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <NewNativeSelectInput
                  label="Article Assignment"
                  name="articleTaskManagement"
                  value={userdata.articleTaskManagement}
                  onChange={handleInputChange}
                  options={permissionOptions}
                  required
                  error={errors.articleTaskManagement}
                />
                <NewNativeSelectInput
                  label="Issue Task Assignment"
                  name="issueTaskManagement"
                  value={userdata.issueTaskManagement}
                  onChange={handleInputChange}
                  options={permissionOptions}
                  error={errors.issueTaskManagement}
                  required
                />
                <NewNativeSelectInput
                  label="Issue Overview"
                  name="issueOverview"
                  value={userdata.issueOverview}
                  onChange={handleInputChange}
                  options={permissionOptions}
                  error={errors.issueOverview}
                  required
                />
                <NewNativeSelectInput
                  label="Journal Overview"
                  name="articleOverview"
                  value={userdata.articleOverview}
                  onChange={handleInputChange}
                  options={permissionOptions}
                  error={errors.articleOverview}
                  required
                />
                <NewNativeSelectInput
                  label="Settings"
                  name="settings"
                  value={userdata.settings}
                  onChange={handleInputChange}
                  options={permissionOptions}
                  error={errors.settings}
                  required
                />
              </div>
            </>
          )}
        </form>
      </div>

      {/* footer section */}
      <div className="p-4 border-t sticky bottom-0 bg-white z-10">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={oncancel}
            className="text-[#1C40C9] px-4 py-2 rounded-md hover:bg-gray-50 transition-colors mr-4"
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`px-4 py-2 rounded-md transition-colors ${
              isDataChanged() && !isSubmitting
                ? "bg-[#1C40C9] text-white hover:bg-[#1C40C9]"
                : "bg-gray-300 text-gray-600 cursor-not-allowed"
            }`}
            onClick={handleSubmit}
            disabled={!isDataChanged() || isSubmitting}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export { EditUserForm };
